import { cn } from "@/lib/utils";
import { FieldLabel } from "./FieldLabel";
import { isInlineField } from "./fieldHelpers";
import { JSONSchemaFieldTemplateProps } from "./types";

/**
 * Default field template for the JSON schema form: renders a field's label,
 * child input, and error, choosing an inline (label left / input right) or
 * block layout, and promoting titled object/array containers to section
 * headers so the form gains visual hierarchy.
 * @param props - Field id, label/description, required/error state, child input, and schema
 * @returns The rendered field wrapper
 */
export function JSONSchemaFieldTemplate<FormContextType>({
  id,
  label,
  description,
  required,
  error,
  children,
  className,
  schema,
}: JSONSchemaFieldTemplateProps<FormContextType>) {
  const inline = isInlineField(schema);
  const isCalculation =
    typeof schema === "object" &&
    schema !== null &&
    schema.format === "fpCalculation";

  if (inline) {
    return (
      <div className={cn("py-2", className)}>
        {/* The row wraps rather than squeezes: a pinned input collapsed the
            label's box while its text stayed, printing the label over the
            input. The label's basis is fixed rather than its text width, so
            every field in a form crosses the wrap point together instead of
            leaving the list half stacked and half squeezed. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
          {label && (
            <div className="min-w-0 grow basis-40">
              <FieldLabel
                id={id}
                label={label}
                required={required}
                description={description}
                calc={isCalculation}
              />
            </div>
          )}
          <div className="min-w-[120px] shrink basis-[300px]">{children}</div>
        </div>
        {error && <p className="text-destructive text-xs mt-1">{error}</p>}
      </div>
    );
  }

  // Object/array fields are containers — render their title as a section
  // header (eyebrow) with a little extra top room, so groups like "Outside
  // Services" read as headings rather than blending in with leaf-field labels.
  const schemaType =
    typeof schema === "object" && schema !== null
      ? Array.isArray(schema.type)
        ? schema.type[0]
        : schema.type
      : undefined;
  // Only treat as a section when the container actually has a title. Untitled
  // containers (e.g. the object inside each array row) must NOT get section
  // margins/headers, or every row picks up stray top spacing.
  const isTitledSection =
    (schemaType === "object" || schemaType === "array") && Boolean(label);

  return (
    <div
      className={cn(
        "space-y-1.5",
        // Untitled containers (the object inside each array row) skip their own
        // vertical padding — the leaf fields inside already pad themselves.
        label ? "py-2" : undefined,
        // Titled sections breathe above and below so they separate from the
        // surrounding fields (e.g. the Outside Services array and what follows).
        isTitledSection && "my-6",
        className,
      )}
    >
      {label &&
        (isTitledSection ? (
          <div className="border-b border-border/80 pb-1.5">
            <FieldLabel
              id={id}
              label={label}
              required={required}
              description={description}
              variant="header"
            />
          </div>
        ) : (
          <FieldLabel
            id={id}
            label={label}
            required={required}
            description={description}
            variant="field"
            calc={isCalculation}
          />
        ))}
      {children}
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
}
